import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicApi } from '../utils/api';
import LoadingOverlay from '../components/LoadingOverlay';

const AUTH_CONFIG = {
  tokenStorageKey: 'admin-auth-token',
  tokenExpiryKey: 'admin-auth-expiry',
};

function isAuthenticated(): boolean {
  try {
    const token = localStorage.getItem(AUTH_CONFIG.tokenStorageKey);
    const expiry = localStorage.getItem(AUTH_CONFIG.tokenExpiryKey);
    if (!token || !expiry) return false;
    if (Date.now() > parseInt(expiry, 10)) {
      localStorage.removeItem(AUTH_CONFIG.tokenStorageKey);
      localStorage.removeItem(AUTH_CONFIG.tokenExpiryKey);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

async function authenticate(username: string, password: string): Promise<{ ok: boolean; message?: string }> {
  try {
    const res = await publicApi.post('/api/admin/auth/login', { username, password });
    const token: string = res.data.token;
    const expiresInMs: number = res.data.expiresInMs;
    const expiry = `${Date.now() + (Number.isFinite(expiresInMs) ? expiresInMs : 0)}`;
    localStorage.setItem(AUTH_CONFIG.tokenStorageKey, token);
    localStorage.setItem(AUTH_CONFIG.tokenExpiryKey, expiry);
    return { ok: true };
  } catch (e: any) {
    const msg = e?.response?.data?.error || 'ログインに失敗しました。';
    return { ok: false, message: msg };
  }
}

const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Admin Login - Takagi Portal';
    if (isAuthenticated()) {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      setError('ユーザー名とパスワードを入力してください。');
      return;
    }
    const result = await authenticate(username, password);
    if (result.ok) {
      setError(null);
      navigate('/admin', { replace: true });
    } else {
      setError(result.message || 'ユーザー名またはパスワードが無効です。');
    }
  };

  return (
    <div style={{
      fontFamily: 'Noto Sans JP, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif',
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      flexDirection: 'column',
      backgroundColor: '#fff', margin: 0, padding: '1rem'
    }}>
      <LoadingOverlay />
      <div><img src="/logo/logo.svg" alt="Takagi Logo" style={{ width: 180, marginBottom: '1.5rem' }} /></div>
      <div style={{ backgroundColor: '#f5f5f5', padding: '2rem', borderRadius: 8, boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: 350, maxWidth: '90%', textAlign: 'center' }}>
        <h2 style={{ marginTop: 0, color: '#333', marginBottom: '1.5rem', fontWeight: 700 }}>ログイン</h2>
        <form onSubmit={onSubmit}>
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <label htmlFor="username" style={{ display: 'block', marginBottom: '.5rem', fontWeight: 500 }}>アカウントID（メール）:</label>
            <input id="username" type="text" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off"
                   style={{ width: '100%', padding: '.8rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '.5rem', fontWeight: 500 }}>パスワード:</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                   style={{ width: '100%', padding: '.8rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }} />
          </div>
          {error && (
            <div style={{ color: '#e74c3c', marginTop: '1rem', fontSize: '.9rem' }}>{error}</div>
          )}
          <button type="submit" style={{ width: '100%', backgroundColor: '#00a0e9', color: '#fff', border: 'none', padding: '.8rem', borderRadius: 4, cursor: 'pointer', fontSize: '1rem', fontWeight: 500, marginTop: '1rem' }}
            onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#0080c7')}
            onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#00a0e9')}
          >ログイン</button>
          <div style={{ marginTop: '0.75rem', textAlign: 'right' }}>
            <a href="/admin/forgot-password" style={{ color: '#0077C8', textDecoration: 'none' }}>パスワードをお忘れの場合</a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;
